// 網頁推播（Firebase Cloud Messaging）。
// - 「有設定才啟用」：.env 沒有 VUE_APP_FIREBASE_* 或瀏覽器不支援時，isSupported() 是 false，其餘函式不會做任何事。
// - 不裝 firebase 套件：要用到時才從 Google 的 CDN 載入 SDK（compat 版），不影響一般頁面的載入。
// - 推播的 service worker 用獨立範圍（BASE + 'fcm-push/'），不跟 PWA 的 service worker 衝突。
// - 瀏覽器規定推播只能在 https 或 localhost 運作。
import * as Api from '@/util/http_api';

const SDK_VERSION = '10.12.2';
const BASE = process.env.BASE_URL || '/';
const TOKEN_KEY = 'vue@push_token';

const CONFIG = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
};
const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY;

const readToken = () => {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (e) { return ''; }
};
const writeToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token); else localStorage.removeItem(TOKEN_KEY);
  } catch (e) { /* 無痕模式等情況存不了，不影響功能 */ }
};

export const isConfigured = () => !!(CONFIG.apiKey && CONFIG.projectId && CONFIG.messagingSenderId && CONFIG.appId && VAPID_KEY);

export const isSupported = () => {
  if (typeof window === 'undefined') return false;
  if (!isConfigured()) return false;
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return false;
  return window.isSecureContext === true || location.hostname === 'localhost';
};

// 'granted'（已允許）／'denied'（被封鎖）／'default'（還沒問過）／'unsupported'
export const permission = () => (isSupported() ? Notification.permission : 'unsupported');

// 這個瀏覽器目前是否已登記推播（允許通知、而且有 token）
export const isEnabled = () => isSupported() && Notification.permission === 'granted' && readToken() !== '';

const loadScript = (src) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[data-push-sdk="${src}"]`);
  if (existing) {
    if (existing.dataset.loaded === '1') resolve(); else existing.addEventListener('load', () => resolve());
    return;
  }
  const el = document.createElement('script');
  el.src = src;
  el.async = true;
  el.dataset.pushSdk = src;
  el.onload = () => { el.dataset.loaded = '1'; resolve(); };
  el.onerror = () => reject(new Error('無法載入 Firebase SDK：' + src));
  document.head.appendChild(el);
});

let messagingPromise;
const getMessaging = () => {
  if (!messagingPromise) {
    messagingPromise = (async () => {
      await loadScript(`https://www.gstatic.com/firebasejs/${SDK_VERSION}/firebase-app-compat.js`);
      await loadScript(`https://www.gstatic.com/firebasejs/${SDK_VERSION}/firebase-messaging-compat.js`);
      if (!window.firebase.apps.length) window.firebase.initializeApp(CONFIG);
      const query = Object.keys(CONFIG).map(k => `${k}=${encodeURIComponent(CONFIG[k])}`).join('&');
      const registration = await navigator.serviceWorker.register(`${BASE}firebase-messaging-sw.js?${query}`, {scope: `${BASE}fcm-push/`});
      // 等 service worker 啟用後才能訂閱推播
      if (!registration.active) {
        await new Promise((resolve) => {
          const worker = registration.installing || registration.waiting;
          if (!worker) { resolve(); return; }
          worker.addEventListener('statechange', () => { if (worker.state === 'activated') resolve(); });
        });
      }
      return {messaging: window.firebase.messaging(), registration};
    })().catch((e) => { messagingPromise = undefined; throw e; });
  }
  return messagingPromise;
};

const fetchAndRegisterToken = async () => {
  const {messaging, registration} = await getMessaging();
  const token = await messaging.getToken({vapidKey: VAPID_KEY, serviceWorkerRegistration: registration});
  if (!token) throw new Error('取不到推播 token');
  await Api.registerPushToken(token);
  writeToken(token);
  return token;
};

// 使用者按「開啟推播」：要求通知權限 → 取 token → 登記到後端
export const enable = async () => {
  if (!isSupported()) return {ok: false, reason: 'unsupported'};
  const result = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (result !== 'granted') return {ok: false, reason: result};
  try {
    await fetchAndRegisterToken();
    return {ok: true};
  } catch (e) {
    console.error(e);
    return {ok: false, reason: 'error', error: e};
  }
};

// 已經允許過通知時，登入後（或換帳號後）重新登記一次，確保 token 掛在目前登入的帳號下。不會跳出權限詢問。
export const syncIfGranted = async () => {
  if (!isSupported() || Notification.permission !== 'granted') return false;
  try {
    await fetchAndRegisterToken();
    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
};

// 取消登記（使用者關閉，或登出時）。後端那筆刪掉＋瀏覽器端的 token 也作廢，不會再收到推播。
export const disable = async () => {
  const token = readToken();
  writeToken('');
  if (!isSupported()) return;
  try {
    if (token) await Api.unregisterPushToken(token);
  } catch (e) {
    console.error(e);
  }
  try {
    if (messagingPromise) {
      const {messaging} = await messagingPromise;
      await messaging.deleteToken();
    }
  } catch (e) {
    console.error(e);
  }
};

// 頁面在前景時收到推播（service worker 不會顯示，改由頁面處理）
export const onForegroundMessage = async (callback) => {
  if (!isSupported() || Notification.permission !== 'granted') return;
  try {
    const {messaging} = await getMessaging();
    messaging.onMessage(callback);
  } catch (e) {
    console.error(e);
  }
};

// 點了推播通知、頁面已經開著時，service worker 會傳訊息過來要求導到對應頁面
export const listenNavigate = (callback) => {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'push-navigate') callback(event.data.path);
  });
};
